import { createSlice } from '@reduxjs/toolkit';
import { AdapterIndexedDB } from './AdapterIndexedDB';
import { AdapterWebSocket } from './AdapterWebSocket';

interface SliceGenericoState {
	iconMenu: boolean;
	showSaludo: boolean;
	pageTitle: string;
	pageSubtitle: string;
	breadcrumbList: Array<{ text: string; navigate: boolean; path: string }>;

	loading: boolean;
	textLoading: string;

	websocket: AdapterWebSocket;

	dbLocal: AdapterIndexedDB;

	online: boolean;
	countProcess: number;

	loadingMaestros: boolean;
	availableUpdate: boolean;
};

const initialState: SliceGenericoState = {
	iconMenu: false,
	showSaludo: true,
	pageTitle: '',
	pageSubtitle: '',
	breadcrumbList: [],

	loading: false,
	textLoading: '',

	websocket: new AdapterWebSocket(process.env.REACT_APP_WS_HOST, Number(process.env.REACT_APP_WS_PORT) || 80, process.env.REACT_APP_WS_SECURE === 'true' ? true : false, process.env.REACT_APP_WS_PATH, {}),

	dbLocal: new AdapterIndexedDB(`db_${process.env.REACT_APP_SISTEMA}_${process.env.REACT_APP_VERSION_MAJOR}`, Number(process.env.REACT_APP_INDEXEDDB_VERSION) || 1, [
		// Aprobaciones
		{ name: 'Comparativo', pk: '_id', index: [], encrpyt: false },
		{ name: 'Factura', pk: '_id', index: [], encrpyt: false },
		{ name: 'RequerimientoSistema', pk: '_id', index: [], encrpyt: false },
		{ name: 'RequerimientoCompra', pk: '_id', index: [], encrpyt: false },
		{ name: 'SolicitudCajaChica', pk: '_id', index: [], encrpyt: false },
		{ name: 'RendicionGastosCajaChica', pk: '_id', index: [], encrpyt: false },
		{ name: 'HojaPeticion', pk: '_id', index: [], encrpyt: false },
		{ name: 'CondicionLaboral', pk: '_id', index: [], encrpyt: false },
		{ name: 'AusenciaAprobacion', pk: '_id', index: [], encrpyt: false },
		{ name: 'DocucobraAprobacion', pk: '_id', index: [], encrpyt: false },
		{ name: 'ValorizacionAprobacion', pk: '_id', index: [], encrpyt: false },

		// Rendiciones
		{ name: 'GastoAprobacion', pk: 'Codigo', index: [], encrpyt: false },
		{ name: 'HistoricoGasto', pk: 'Codigo', index: [], encrpyt: false },
		{ name: 'RendicionGasto', pk: 'Codigo', index: [], encrpyt: false },
		{ name: 'Gastos', pk: 'Codigo', index: [], encrpyt: false },
		{ name: 'SolicitudAnticipo', pk: 'Codigo', index: [], encrpyt: false },
		
		// Tareo
		{ name: 'TareoAprobacion', pk: 'Codigo', index: [], encrpyt: false },
		{ name: 'TareoPersonal', pk: 'Codigo', index: [], encrpyt: false },
		{ name: 'TareoGrupal', pk: 'Codigo', index: [], encrpyt: false },

		// CatalogosRendiciones - CatalogosTareo 
		{ name: 'CTFlujosOT', pk: '_id', index: [], encrpyt: false },
		{ name: 'CTFlujosDelegacion', pk: '_id', index: [], encrpyt: false },
		{ name: 'CTOT', pk: '_id', index: [], encrpyt: false },
		{ name: 'CTProveedor', pk: '_id', index: [], encrpyt: false },
		{ name: 'CTMoneda', pk: '_id', index: [], encrpyt: false },
		{ name: 'CTTipoCambio', pk: '_id', index: [], encrpyt: false },
		{ name: 'CTTipoDocumento', pk: '_id', index: [], encrpyt: false },
		{ name: 'CTServicio', pk: '_id', index: [], encrpyt: false },
		{ name: 'CTVAT', pk: '_id', index: [], encrpyt: false },
		{ name: 'CTTipoDocumentoTributario', pk: '_id', index: [], encrpyt: false },
		{ name: 'CTProyectos', pk: '_id', index: [], encrpyt: false },
		{ name: 'CTPersonal', pk: 'Identificacion', index: [], encrpyt: false },
		{ name: 'CTAnticipo', pk: '_id', index: [], encrpyt: false },
		{ name: 'CTProducto', pk: '_id', index: [], encrpyt: false },
		{ name: 'CTPeriodo', pk: 'Codigo', index: [], encrpyt: false },
		{ name: 'CTDisciplina', pk: 'Code', index: [], encrpyt: false },
		{ name: 'CTTipoAusencia', pk: '_id', index: [], encrpyt: false },
		{ name: 'CTAusencia', pk: 'Codigo', index: [], encrpyt: false },
		{ name: 'CTEmpresas', pk: '_id', index: [], encrpyt: false },
	]),

	online: navigator.onLine,
	countProcess: 0,

	loadingMaestros: true,
	availableUpdate: false
};

initialState.websocket.create();
initialState.websocket.init();

initialState.dbLocal.run();

const genericoSlice = createSlice({
	name: 'genericoSlice',
	initialState,
	reducers: {
		addLoading: (state, action) => {
			return {
				...state,
				loading: true,
				textLoading: action.payload.textLoading,
			};
		},
		removeLoading: state => {
			return {
				...state,
				loading: false,
				textLoading: '',
			};
		},
		showIconMenu: state => {
			return {
				...state,
				iconMenu: true,
			};
		},
		hideIconMenu: state => {
			return {
				...state,
				iconMenu: false,
			};
		},
		changeSaludo: (state, action) => {
			return {
				...state,
				showSaludo: action.payload,
			};
		},
		changePageTitle: (state, action) => {
			return {
				...state,
				pageTitle: action.payload,
			};
		},
		changePageSubtitle: (state, action) => {
			return {
				...state,
				pageSubtitle: action.payload,
			};
		},
		changeBreadcrumbList: (state, action) => {
			return {
				...state,
				breadcrumbList: action.payload,
			};
		},
		changeOnline: (state, action) => {
			return {
				...state,
				online: action.payload,
			};
		},
		changeCountProcess: (state, action) => {
			return {
				...state,
				countProcess: action.payload,
			};
		},
		addCountProcess: (state) => {
			let count: number = state.countProcess;
			count += 1;
			return {
				...state,
				countProcess: count,
			};
		},
		removeLoadingMaestros: (state) => {
			return {
				...state,
				loadingMaestros: false
			};
		},
		changeAvailableUpdate: (state, action) => {
			return {
				...state,
				availableUpdate: action.payload,
			};
		},
	},
});

export const {
	showIconMenu,
	hideIconMenu,
	addLoading,
	removeLoading,
	changeSaludo,
	changePageTitle,
	changePageSubtitle,
	changeBreadcrumbList,
	changeOnline,
	changeCountProcess,
	addCountProcess,
	removeLoadingMaestros,
	changeAvailableUpdate
} = genericoSlice.actions;

export default genericoSlice.reducer;